import React, { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { RULE_ARITHMETIC_FUNCTION_TYPES } from 'yeself-common/enum/RuleEnum';
import { LayerNodeId, LayerId } from 'yeself-common/typings/guide/LayerCommon';

import SelectField from '../../components/SelectField';
import PlainStringField from '../../components/PlainStringField';

import { InteractedWithRuleValues } from '../types';

type InteractedWithRuleFieldProps = {
    value: InteractedWithRuleValues;
    onChange: (value: InteractedWithRuleValues) => void;
    options: {
        value: LayerId;
        label: string;
        options: { value: LayerNodeId; label: string }[];
    }[];
};

const InteractedWithRuleField = ({
    value,
    onChange,
    options = [],
}: InteractedWithRuleFieldProps): React.ReactElement => {
    const [levelValue, setLevelValue] = useState({
        level0: value.level?.[0]?.data.value.value || null,
        level1: value.level?.[1]?.data.value.value || null,
        level2: value.level?.[2]?.data.key || null,
        level3: value.level?.[3]?.data.value || null,
    });

    const handleOnChange = useCallback(
        (index: number) => (newValue) => {
            const newLevelValue = { ...levelValue, [`level${index}`]: newValue };

            const level: InteractedWithRuleFieldProps['value']['level'] = [
                newLevelValue.level0
                    ? { data: { value: { value: newLevelValue.level0, label: '' } } }
                    : undefined,
                newLevelValue.level1
                    ? { data: { value: { value: newLevelValue.level1, label: '' } } }
                    : undefined,
                newLevelValue.level2 ? { data: { key: newLevelValue.level2 } } : undefined,
                newLevelValue.level3 ? { data: { value: newLevelValue.level3 } } : undefined,
            ];

            setLevelValue(newLevelValue);
            onChange({ ...value, level });
        },
        [levelValue, onChange, value]
    );

    const interactionCollectorNodeDataOptions =
        options.find(({ value }) => value === levelValue.level0)?.options || [];

    return (
        <>
            <SelectField
                autoWidth
                options={options}
                value={levelValue.level0 || ''}
                onChange={handleOnChange(0)}
            />
            {levelValue.level0 ? (
                <SelectField
                    autoWidth
                    options={interactionCollectorNodeDataOptions}
                    value={levelValue.level1 || ''}
                    onChange={handleOnChange(1)}
                />
            ) : null}
            {levelValue.level1 ? (
                <SelectField
                    autoWidth
                    options={[
                        {
                            label: <FormattedMessage id="Rules.level.eq" />,
                            value: RULE_ARITHMETIC_FUNCTION_TYPES.EQ,
                        },
                        {
                            label: <FormattedMessage id="Rules.level.gt" />,
                            value: RULE_ARITHMETIC_FUNCTION_TYPES.GT,
                        },
                        {
                            label: <FormattedMessage id="Rules.level.lt" />,
                            value: RULE_ARITHMETIC_FUNCTION_TYPES.LT,
                        },
                    ]}
                    value={levelValue.level2 || ''}
                    onChange={handleOnChange(2)}
                />
            ) : null}
            {levelValue.level2 ? (
                <PlainStringField value={levelValue.level3 || ''} onChange={handleOnChange(3)} />
            ) : null}
        </>
    );
};

export default InteractedWithRuleField;
